import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import FooterCopyright from '../components/FooterCopyright'

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-12">
    <h2 className="mb-4 inline-block border-b border-paper-200 pb-2 text-lg font-semibold text-ink-900 md:text-xl">
      {title}
    </h2>
    <div className="text-[0.9375rem] leading-relaxed text-ink-600">
      {children}
    </div>
  </section>
)

const PrivacyPage: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const EFFECTIVE_DATE = 'February 28, 2026'
  const CONTACT_EMAIL = 'help@treuthat.com'

  return (
    <div className="min-h-screen bg-paper-0">

      {/* Header */}
      <header className="flex h-16 items-center justify-between border-b border-white/10 bg-brand-950 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center">
          <img src="/assets/logo-white.png" alt="Treu" className="h-[26px] w-auto" />
        </Link>
        <Link
          to="/login"
          className="rounded-sm border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
        >
          Sign In
        </Link>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-[720px] px-4 pb-20 pt-12">

        <div className="mb-12">
          <h1 className="mb-2 text-2xl font-bold text-ink-900 md:text-4xl">
            Privacy Policy
          </h1>
          <p className="text-sm text-ink-400">
            Effective Date: {EFFECTIVE_DATE}
          </p>
        </div>

        <p className="mb-12 text-[0.9375rem] leading-relaxed text-ink-600">
          Treu ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains
          what information we collect, how we use it, and your rights regarding your data. By using our Services,
          you agree to the practices described in this policy.
        </p>

        <Section title="1. Information We Collect">
          <p className="mb-4">
            <strong>Account Information:</strong> When you sign up using Google or Apple, we receive your name,
            email address, and profile picture from those providers. We store these to create and manage your account.
          </p>
          <p className="mb-4">
            <strong>Verification Queries:</strong> We store the claims and questions you submit for verification,
            along with the results returned, so you can review your history within the app.
          </p>
          <p className="mb-4">
            <strong>Usage Data:</strong> We collect information about how you interact with the Services —
            including pages visited, features used, and verification counts — to improve the product and enforce
            subscription limits.
          </p>
          <p className="mb-4">
            <strong>Payment Information:</strong> Payment details (credit/debit card information) are processed
            directly by LemonSqueezy. We do not store your full card number or CVV. We receive a subscription
            status and customer identifier from LemonSqueezy for account management purposes.
          </p>
          <p className="mb-4">
            <strong>Device and Log Data:</strong> We may collect your IP address, browser type, operating system,
            and device identifiers when you use the Services. This helps us diagnose technical issues and protect
            account security.
          </p>
          <p>
            <strong>Crash and Performance Data:</strong> We use Sentry, a third-party error tracking service, to
            collect crash reports, error logs, and performance data from our mobile and web applications. This data
            may include device type, operating system version, app state at the time of an error, and anonymised
            usage context. This information is used solely to identify and fix bugs and improve app stability. Sentry
            processes this data under its own privacy policy and data processing agreement.
          </p>
        </Section>

        <Section title="2. How We Use Your Information">
          <p className="mb-2">We use the information we collect to:</p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li>Create and manage your account</li>
            <li>Provide and operate the verification Services</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send you service-related emails (e.g., subscription confirmations, receipts)</li>
            <li>Send optional product update emails if you have opted in to email notifications</li>
            <li>Improve and develop our AI models and verification accuracy</li>
            <li>Detect and prevent fraud, abuse, and security threats</li>
            <li>Comply with our legal obligations</li>
          </ul>
          <p>
            We do not sell your personal data to third parties for advertising purposes, and we do not use your
            verification queries to build advertising profiles.
          </p>
        </Section>

        <Section title="3. Data Retention">
          <p className="mb-4">
            <strong>Verification queries and results</strong> are retained for the lifetime of your account so
            that you can review your history in the app. You may delete individual chat threads at any time
            from within the app; deleted threads are removed immediately and are not recoverable.
          </p>
          <p className="mb-4">
            <strong>Account data</strong> (name, email) is retained while your account is active. When you delete
            your account, your profile data and all associated verification history are permanently removed from our
            systems within 30 days.
          </p>
          <p>
            <strong>Community posts</strong> you author may remain visible after account deletion in anonymised form
            unless you delete them prior to deleting your account.
          </p>
        </Section>

        <Section title="4. Information Sharing">
          <p className="mb-4">
            We share your information only in the following circumstances:
          </p>
          <p className="mb-4">
            <strong>AI Processing Providers:</strong> Your verification queries are sent to third-party AI providers
            (Anthropic Claude and Google Gemini) to generate results. These providers process data under their own
            privacy policies and data processing agreements. We do not share your account identity with these providers.
          </p>
          <p className="mb-4">
            <strong>Payment Processor:</strong> Your payment information is handled by LemonSqueezy, which acts as the
            Merchant of Record. Their privacy policy governs their use of your payment data.
          </p>
          <p className="mb-4">
            <strong>Authentication Providers:</strong> When you sign in with Google or Apple, your identity is verified
            through their OAuth services. We receive only the profile information those providers share with us.
          </p>
          <p className="mb-4">
            <strong>Error Tracking:</strong> Crash reports and performance data are sent to Sentry (Functional
            Software, Inc.) for the purpose of monitoring app stability and fixing bugs. No personal account
            information is included in these reports.
          </p>
          <p className="mb-4">
            <strong>Legal Requirements:</strong> We may disclose your information if required to do so by law,
            court order, or governmental authority, or to protect the rights, property, or safety of Treu, our
            users, or the public.
          </p>
          <p>
            <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your
            information may be transferred to a successor entity, subject to the same privacy commitments.
          </p>
        </Section>

        <Section title="5. Data Security">
          <p>
            We implement appropriate technical and organisational measures to protect your personal data against
            unauthorised access, loss, or disclosure. These include encrypted connections (HTTPS/TLS), hashed
            authentication tokens, and access controls on our backend systems. However, no method of transmission
            over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="6. Cookies and Tracking">
          <p className="mb-4">
            We use session cookies to maintain your authentication state while you are signed in. These are
            essential for the Services to function and are deleted when you sign out or close your browser session.
          </p>
          <p>
            We do not use third-party advertising cookies or cross-site tracking technologies.
          </p>
        </Section>

        <Section title="7. Your Privacy Rights">
          <p className="mb-4">
            Depending on your location, you may have the following rights regarding your personal data:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate personal data</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data (available directly in the app)</li>
            <li><strong>Data Portability:</strong> Request an export of your data in a machine-readable format</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time via the unsubscribe link or your account settings</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent-600 underline transition-colors hover:text-accent-500">{CONTACT_EMAIL}</a>.
            We will respond within 30 days.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            The Services are not directed at children under the age of 13. We do not knowingly collect personal
            information from children under 13. If you believe we have inadvertently collected such information,
            please contact us at {CONTACT_EMAIL} and we will promptly delete it.
          </p>
        </Section>

        <Section title="9. International Data Transfers">
          <p>
            Treu operates from the United States. If you access the Services from outside the United States, your
            information may be transferred to, processed, and stored in the United States, where data protection
            laws may differ from those in your country. By using the Services, you consent to such transfers.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the Effective Date above.
            For material changes, we will notify you by email or via an in-app notice. We encourage you to review
            this policy periodically.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy, please contact our
            privacy team at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent-600 underline transition-colors hover:text-accent-500">{CONTACT_EMAIL}</a>
          </p>
        </Section>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-brand-950 px-4 py-8 text-center">
        <div className="mb-4 flex flex-wrap justify-center gap-8">
          <Link to="/" className="text-sm text-white/50 transition-colors hover:text-white/80">Home</Link>
          <Link to="/terms" className="text-sm text-white/50 transition-colors hover:text-white/80">Terms of Service</Link>
          <Link to="/contact" className="text-sm text-white/50 transition-colors hover:text-white/80">Contact</Link>
        </div>
        <FooterCopyright variant="full" className="text-white/50" />
      </footer>

    </div>
  )
}

export default PrivacyPage
